import { parseISO } from 'date-fns';
import React from 'react';
import AdminContainer from '../../Utility/AdminContainer';
import InfoBox from './InfoCard';
import PaginatedCard from './PaginatedCard';
import { Button } from '../../Utility/Button';
import Chart from 'react-apexcharts';
import useAdminDashboard from '../../../Hooks/useAdminDashboard';
import { Badge } from '../../Utility/Badge';
import LoadingSpinner from '../../Utility/LoadingSpinner';
import {
    CostsPerAssistantType,
    CostsPerChatModelType,
    CostsPerEmbeddingModelType,
    CostsPerVoiceModelType,
    CostsPerPromptFunctionType,
    CostsPerUserType,
    CostsPerGroupType,
    DlpTrackCountPerGroupType,
} from '../../../Types';
import Avatar from '../../Utility/Avatar';
import { ChatMessageRole } from '../../../Types/Enums';
import { RootState, useAppSelector } from '../../../store';
import { PaginationListType } from '../../../Types/PaginationParams';
import DateRangePicker from '../../Utility/DateRangePicker';

const Dashboard = () => {
    const settings = useAppSelector((state: RootState) => state.settings);
    const {
        DashboardActions,
        dashboardloading,
        timeRange,
        selectedLanguage,
        i18n,
        showUserStatistics,
        dashboardData,
        prePaidActive,
        getCostsPerUser,
        getCostsPerGroup,
        getCostsPerAssistant,
        getCostsPerChatModel,
        getCostsPerEmbeddingModel,
        getCostsPerVoiceModel,
        getCostsPerPromptFunction,
    } = useAdminDashboard();

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <div className="md:flex md:justify-between">
                    <DateRangePicker
                        timeRange={timeRange}
                        selectedLanguage={selectedLanguage}
                        i18n={i18n}
                        onTimeRangeChange={(range: { from: Date; to: Date }) => {
                            DashboardActions.setTimeRangeWithTime({ from: range.from, to: range.to });
                        }}
                    />
                    <Button variant="primary" className="mt-3 md:mt-0" onClick={DashboardActions.exportData}>
                        {i18n.translate({ id: 'exportButton', sourceName: 'Dashboard' })}
                    </Button>
                </div>

                <div>
                    <div
                        className={`grid ${
                            settings.prePaidActive ? 'grid-cols-12' : 'grid-cols-12 xl:grid-cols-9'
                        } gap-4 mt-4`}
                    >
                        <div className="col-span-12 md:col-span-6 xl:col-span-3">
                            <InfoBox
                                title={i18n.translate({ id: 'title.licencesUsed', sourceName: 'Dashboard' })}
                                smallTitle={true}
                                loading={dashboardloading.licences}
                                value={dashboardData.licencesUsed}
                                badgeText={DashboardActions.getLicenceBadgeText(dashboardData.licencesFree, i18n)}
                                badgeVariant={DashboardActions.getLicenceBadgeVariant(dashboardData.licencesFree)}
                            />
                        </div>
                        {prePaidActive && (
                            <div className="col-span-12 md:col-span-6 xl:col-span-3">
                                <InfoBox
                                    title={i18n.translate({ id: 'title.credit', sourceName: 'Dashboard' })}
                                    smallTitle={true}
                                    loading={dashboardloading.totalCosts || dashboardloading.totalCredit}
                                    value={
                                        dashboardData.balance >= 0
                                            ? DashboardActions.valueFormatter(dashboardData.balance)
                                            : '-'
                                    }
                                />
                            </div>
                        )}
                        <div className="col-span-12 md:col-span-6 xl:col-span-3">
                            <InfoBox
                                title={i18n.translate({ id: 'title.timeRange', sourceName: 'Dashboard' })}
                                smallTitle={true}
                                loading={dashboardloading.timeRangeCosts && dashboardloading.previousTimeRangeCosts}
                                value={
                                    dashboardData.totalCostsTimeRange >= 0
                                        ? DashboardActions.valueFormatter(dashboardData.totalCostsTimeRange)
                                        : '-'
                                }
                                previousValue={
                                    dashboardData.previousTotalCostsTimeRange >= 0 &&
                                    dashboardData.totalCostsTimeRange !== dashboardData.previousTotalCostsTimeRange
                                        ? DashboardActions.valueFormatter(dashboardData.previousTotalCostsTimeRange)
                                        : null
                                }
                                previousLabel={i18n.translate({ id: 'previousTimeRange', sourceName: 'Dashboard' })}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-6 xl:col-span-3">
                            <InfoBox
                                title={i18n.translate({ id: 'title.perUser', sourceName: 'Dashboard' })}
                                smallTitle={true}
                                loading={
                                    dashboardloading.timeRangeAvgCosts && dashboardloading.previousTimeRangeAvgCosts
                                }
                                value={
                                    dashboardData.totalCostsAvgTimeRange >= 0
                                        ? DashboardActions.valueFormatter(dashboardData.totalCostsAvgTimeRange)
                                        : '-'
                                }
                                previousValue={
                                    dashboardData.previousTotalCostsAvgTimeRange >= 0 &&
                                    dashboardData.totalCostsAvgTimeRange !==
                                        dashboardData.previousTotalCostsAvgTimeRange
                                        ? DashboardActions.valueFormatter(dashboardData.previousTotalCostsAvgTimeRange)
                                        : null
                                }
                                previousLabel={i18n.translate({ id: 'previousTimeRange', sourceName: 'Dashboard' })}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className={`col-span-12 lg:col-span-6`}>
                            <InfoBox
                                title={i18n.translate({ id: 'title.costsPerDay', sourceName: 'Dashboard' })}
                                value=""
                                loading={dashboardloading.costsPerDay}
                            >
                                <div className="apexcharts-xaxistooltip-bottom apexcharts-tooltip hidden" />
                                <Chart
                                    options={{
                                        colors: [
                                            getComputedStyle(document.documentElement).getPropertyValue(
                                                '--tw-color-primary',
                                            ),
                                        ],
                                        chart: {
                                            toolbar: {
                                                show: false,
                                            },
                                            width: 500,
                                            zoom: {
                                                enabled: false,
                                            },
                                        },
                                        xaxis: {
                                            type: 'datetime',
                                            labels: {
                                                rotate: 0,
                                                style: {
                                                    cssClass: 'chartText text-[12px]',
                                                },
                                                hideOverlappingLabels: true,
                                                format: 'dd.MM.',
                                                datetimeUTC: false,
                                                datetimeFormatter: {
                                                    year: 'yyyy',
                                                    month: 'MM',
                                                    day: 'dd',
                                                    hour: 'HH:mm',
                                                },
                                            },
                                        },
                                        yaxis: {
                                            labels: {
                                                style: {
                                                    cssClass: 'chartText text-[12px]',
                                                },
                                                formatter: (value) => {
                                                    return '$ ' + value.toFixed(2);
                                                },
                                            },
                                            min: 0,
                                            max: Math.ceil(
                                                Math.max(...dashboardData.perDay.map((day: any) => day.costs)),
                                            ),
                                        },
                                        fill: {
                                            type: 'gradient',
                                            gradient: {
                                                shadeIntensity: 1,
                                                opacityFrom: 0.6,
                                                opacityTo: 0.9,
                                                stops: [0, 80, 100],
                                                gradientToColors: [
                                                    getComputedStyle(document.documentElement).getPropertyValue(
                                                        '--tw-color-box-background',
                                                    ),
                                                ],
                                            },
                                        },
                                        dataLabels: {
                                            enabled: false,
                                        },
                                    }}
                                    type="area"
                                    height={350}
                                    series={[
                                        {
                                            name: i18n.translate({
                                                id: 'title.costs',
                                                sourceName: 'Dashboard',
                                            }),
                                            data: dashboardData.perDay.map((day: any, index: number) => ({
                                                x: parseISO(day.date).toUTCString(),
                                                y: Math.round(day.costs * 100) / 100,
                                            })),
                                        },
                                    ]}
                                    style={{ width: '100%' }}
                                />
                            </InfoBox>
                        </div>
                        <div className="col-span-12 lg:col-span-6">
                            <PaginatedCard<CostsPerGroupType>
                                listType={PaginationListType.CostsPerGroup}
                                title={i18n.translate({ id: 'title.costPerGroup', sourceName: 'Dashboard' })}
                                itemsFunction={getCostsPerGroup}
                                HeaderSlot={
                                    <Badge variant="info" className="mb-3">
                                        <p className="m-0">
                                            {i18n.translate({ id: 'text.costPerGroup', sourceName: 'Dashboard' })}
                                        </p>
                                    </Badge>
                                }
                                renderItem={(group: CostsPerGroupType, index: number) => (
                                    <div key={index} className="flex justify-between gap-3">
                                        <p className="truncate text-ellipsis">
                                            {group.title}
                                            <br />
                                            <span className="text-gray-500 truncate text-ellipsis">
                                                {group.userCount +
                                                    ' ' +
                                                    i18n.translate({ id: 'users', sourceName: 'Dashboard' })}
                                            </span>
                                        </p>
                                        <p
                                            className="whitespace-nowrap tooltip"
                                            data-tooltip-content={group.costs.toString()}
                                        >
                                            {DashboardActions.valueFormatter(group.costs)}
                                        </p>
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <PaginatedCard<CostsPerAssistantType>
                                listType={PaginationListType.CostsPerAssistant}
                                title={i18n.translate({ id: 'title.costsPerAssistant', sourceName: 'Dashboard' })}
                                itemsFunction={getCostsPerAssistant}
                                renderItem={(assistant: CostsPerAssistantType, index: number) => (
                                    <div key={index} className="flex justify-between gap-3">
                                        <p className="truncate text-ellipsis pl-[40px] leading-[35px] relative">
                                            <Avatar
                                                size={30}
                                                title={assistant.deletedCount != null ? '-' : (assistant.name ?? '')}
                                                role={ChatMessageRole.assistant}
                                                avatarImage={assistant.image}
                                                position="absolute"
                                            />
                                            {assistant.deletedCount != null ? (
                                                <i>
                                                    {i18n.translate({
                                                        id: 'deletedCount',
                                                        sourceName: 'Dashboard',
                                                        params: { count: assistant.deletedCount },
                                                    })}
                                                </i>
                                            ) : assistant.deleted ? (
                                                <i>
                                                    {assistant.name} (
                                                    {i18n.translate({ id: 'deleted', sourceName: 'Dashboard' })})
                                                </i>
                                            ) : (
                                                assistant.name
                                            )}
                                        </p>
                                        <p
                                            className="whitespace-nowrap tooltip"
                                            data-tooltip-content={assistant.costs.toString()}
                                        >
                                            {DashboardActions.valueFormatter(assistant.costs)}
                                        </p>
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <PaginatedCard<CostsPerChatModelType>
                                listType={PaginationListType.CostsPerChatModel}
                                title={i18n.translate({ id: 'title.costsPerChatModel', sourceName: 'Dashboard' })}
                                itemsFunction={getCostsPerChatModel}
                                renderItem={(chatModel: CostsPerChatModelType, index: number) => (
                                    <div key={index} className="flex justify-between gap-3">
                                        <p className="truncate text-ellipsis pl-[40px] leading-[35px] relative">
                                            <Avatar
                                                size={30}
                                                title={chatModel.deletedCount != null ? '-' : (chatModel.title ?? '')}
                                                role={ChatMessageRole.assistant}
                                                avatarImage={chatModel.image}
                                                position="absolute"
                                            />
                                            {chatModel.deletedCount != null ? (
                                                <i>
                                                    {i18n.translate({
                                                        id: 'deletedCount',
                                                        sourceName: 'Dashboard',
                                                        params: { count: chatModel.deletedCount },
                                                    })}
                                                </i>
                                            ) : chatModel.deleted ? (
                                                <i>
                                                    {chatModel.title} (
                                                    {i18n.translate({ id: 'deleted', sourceName: 'Dashboard' })})
                                                </i>
                                            ) : (
                                                chatModel.title
                                            )}
                                        </p>
                                        <p
                                            className="whitespace-nowrap tooltip"
                                            data-tooltip-content={chatModel.costs.toString()}
                                        >
                                            {DashboardActions.valueFormatter(chatModel.costs)}
                                        </p>
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <PaginatedCard<CostsPerEmbeddingModelType>
                                listType={PaginationListType.CostsPerEmbeddingModel}
                                title={i18n.translate({ id: 'title.costsPerEmbeddingModel', sourceName: 'Dashboard' })}
                                itemsFunction={getCostsPerEmbeddingModel}
                                renderItem={(embeddingModel: CostsPerEmbeddingModelType, index: number) => (
                                    <div key={index} className="flex justify-between gap-3">
                                        <p className="truncate text-ellipsis pl-[40px] leading-[35px] relative">
                                            <Avatar
                                                size={30}
                                                title={embeddingModel.deletedCount != null ? '-' : (embeddingModel.title ?? '')}
                                                role={ChatMessageRole.assistant}
                                                avatarImage={embeddingModel.image}
                                                position="absolute"
                                            />
                                            {embeddingModel.deletedCount != null ? (
                                                <i>
                                                    {i18n.translate({
                                                        id: 'deletedCount',
                                                        sourceName: 'Dashboard',
                                                        params: { count: embeddingModel.deletedCount },
                                                    })}
                                                </i>
                                            ) : embeddingModel.deleted ? (
                                                <i>
                                                    {embeddingModel.title} (
                                                    {i18n.translate({ id: 'deleted', sourceName: 'Dashboard' })})
                                                </i>
                                            ) : (
                                                embeddingModel.title
                                            )}
                                        </p>
                                        <p
                                            className="whitespace-nowrap tooltip"
                                            data-tooltip-content={embeddingModel.costs.toString()}
                                        >
                                            {DashboardActions.valueFormatter(embeddingModel.costs)}
                                        </p>
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <PaginatedCard<CostsPerVoiceModelType>
                                listType={PaginationListType.CostsPerVoiceModel}
                                title={i18n.translate({ id: 'title.costsPerVoiceModel', sourceName: 'Dashboard' })}
                                itemsFunction={getCostsPerVoiceModel}
                                renderItem={(voiceModel: CostsPerVoiceModelType, index: number) => (
                                    <div key={index} className="flex justify-between gap-3">
                                        <p className="truncate text-ellipsis pl-[40px] leading-[35px] relative">
                                            <Avatar
                                                size={30}
                                                title={voiceModel.deletedCount != null ? '-' : (voiceModel.title ?? '')}
                                                role={ChatMessageRole.assistant}
                                                avatarImage={voiceModel.image}
                                                position="absolute"
                                            />
                                            {voiceModel.deletedCount != null ? (
                                                <i>
                                                    {i18n.translate({
                                                        id: 'deletedCount',
                                                        sourceName: 'Dashboard',
                                                        params: { count: voiceModel.deletedCount },
                                                    })}
                                                </i>
                                            ) : voiceModel.deleted ? (
                                                <i>
                                                    {voiceModel.title} (
                                                    {i18n.translate({ id: 'deleted', sourceName: 'Dashboard' })})
                                                </i>
                                            ) : (
                                                voiceModel.title
                                            )}
                                        </p>
                                        <p
                                            className="whitespace-nowrap tooltip"
                                            data-tooltip-content={voiceModel.costs.toString()}
                                        >
                                            {DashboardActions.valueFormatter(voiceModel.costs)}
                                        </p>
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <PaginatedCard<CostsPerPromptFunctionType>
                                listType={PaginationListType.CostsPerPromptFunction}
                                title={i18n.translate({ id: 'title.costsPerConnector', sourceName: 'Dashboard' })}
                                itemsFunction={getCostsPerPromptFunction}
                                renderItem={(promptFunction: CostsPerPromptFunctionType, index: number) => (
                                    <div key={index} className="flex justify-between gap-3">
                                        <p className="truncate text-ellipsis pl-[40px] leading-[35px] relative">
                                            <Avatar
                                                size={30}
                                                title={promptFunction.deletedCount != null ? '-' : (promptFunction.title ?? '')}
                                                role={ChatMessageRole.assistant}
                                                avatarImage={promptFunction.image}
                                                position="absolute"
                                            />
                                            {promptFunction.deletedCount != null ? (
                                                <i>
                                                    {i18n.translate({
                                                        id: 'deletedCount',
                                                        sourceName: 'Dashboard',
                                                        params: { count: promptFunction.deletedCount },
                                                    })}
                                                </i>
                                            ) : promptFunction.deleted ||
                                              ['code_interpreter', 'hidden_kb'].includes(promptFunction.type) ? (
                                                <i>
                                                    {promptFunction.title}
                                                    {promptFunction.deleted &&
                                                        ' (' +
                                                            i18n.translate({ id: 'deleted', sourceName: 'Dashboard' }) +
                                                            ')'}
                                                </i>
                                            ) : (
                                                <>
                                                    {promptFunction.title}{' '}
                                                    {promptFunction.type === 'knowledgebase' && (
                                                        <i>
                                                            {' '}
                                                            (
                                                            {i18n.translate({
                                                                id: 'title',
                                                                sourceName: 'Knowledgebase',
                                                            })}
                                                            )
                                                        </i>
                                                    )}
                                                </>
                                            )}
                                        </p>
                                        <p
                                            className="whitespace-nowrap tooltip"
                                            data-tooltip-content={promptFunction.costs.toString()}
                                        >
                                            {DashboardActions.valueFormatter(promptFunction.costs)}
                                        </p>
                                    </div>
                                )}
                            />
                        </div>

                        {showUserStatistics && (
                            <div className="col-span-12 lg:col-span-6">
                                <PaginatedCard<CostsPerUserType>
                                    listType={PaginationListType.CostsPerUser}
                                    title={i18n.translate({ id: 'title.costPerUser', sourceName: 'Dashboard' })}
                                    itemsFunction={getCostsPerUser}
                                    renderItem={(user: CostsPerUserType, index: number) => (
                                        <div key={index} className="flex justify-between gap-3">
                                            <p className="truncate text-ellipsis pl-[40px] leading-[35px] relative">
                                                <Avatar
                                                    size={30}
                                                    title={
                                                        user.deletedCount != null
                                                            ? '-'
                                                            : `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim() || user.email
                                                    }
                                                    role={ChatMessageRole.assistant}
                                                    avatarImage={undefined}
                                                    position="absolute"
                                                />
                                                {user.deletedCount != null ? (
                                                    <i>
                                                        {i18n.translate({
                                                            id: 'deletedCount',
                                                            sourceName: 'Dashboard',
                                                            params: { count: user.deletedCount },
                                                        })}
                                                    </i>
                                                ) : user.deleted ? (
                                                    <>
                                                        <i>{i18n.translate({ id: 'deleted', sourceName: 'Dashboard' })}</i>
                                                        <br />
                                                        <span className="text-gray-500 truncate text-ellipsis">-</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        {user.givenName} {user.familyName}
                                                        <br />
                                                        <span className="text-gray-500 truncate text-ellipsis">
                                                            {user.email}
                                                        </span>
                                                    </>
                                                )}
                                            </p>
                                            <p
                                                className="whitespace-nowrap tooltip"
                                                data-tooltip-content={user.costs.toString()}
                                            >
                                                {DashboardActions.valueFormatter(user.costs)}
                                            </p>
                                        </div>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </AdminContainer>
        </div>
    );
};

export default Dashboard;
